
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

interface TeamMember {
  id: string;
  full_name: string;
  email: string;
}

export const useTeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [userIsSupervisor, setUserIsSupervisor] = useState(false);

  const checkUserRole = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return;
      }

      const { data: teamMembers, error } = await supabase
        .from('users')
        .select('id')
        .eq('supervisor_id', user.id);

      if (error) {
        console.error("Error checking user role:", error);
        return;
      }

      setUserIsSupervisor((teamMembers || []).length > 0);
    } catch (error) {
      console.error("Error checking user role:", error);
    }
  };

  const loadTeamMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return;
      }

      const { data: members, error } = await supabase
        .from('users')
        .select('id, full_name, email')
        .eq('supervisor_id', user.id);

      if (error) {
        console.error("Error loading team members:", error);
        return;
      }

      const sortedMembers = (members || []).sort((a, b) => {
        const nameA = a.full_name || a.email;
        const nameB = b.full_name || b.email;
        return nameA.localeCompare(nameB);
      });

      setTeamMembers(sortedMembers);
    } catch (error) {
      console.error("Error loading team members:", error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  useEffect(() => {
    checkUserRole();
    loadTeamMembers();
  }, []);

  return {
    teamMembers,
    isLoadingMembers,
    userIsSupervisor,
    loadTeamMembers,
  };
};
